import fragmentShader from "@shaders/hero.frag.glsl";
import vertexShader from "@shaders/hero.vert.glsl";
import type { CameraKeyframe } from "@world/Camera";
import * as THREE from "three";
import { SceneBase } from "./SceneBase";

/**
 * Hero — the opening shot.
 *
 * Migrated from legacy IIFE at lines 2787-2906. Key changes:
 *  - The terrain plane lives in the master scene at z=0 (camera starts at +4 looking in).
 *  - Uniforms include uProgress so the scene visually settles as the user scrolls past it.
 *  - No private renderer / canvas — uses World's shared resources.
 *  - Mouse and scroll velocity uniforms are driven from outside (main.ts) via update().
 */
export class HeroScene extends SceneBase {
  readonly keyframes: CameraKeyframe[] = [
    // Open wide — looking at terrain from above-forward
    { position: [0, 0.4, 4], target: [0, 0, 0], fov: 35 },
    // Dolly in slightly + drop FOV for cinematic close
    { position: [0, 0.1, 2.6], target: [0, 0, 0], fov: 30 },
  ];

  readonly range: [number, number] = [0, 0.18];

  private mesh!: THREE.Mesh<THREE.PlaneGeometry, THREE.ShaderMaterial>;
  private uniforms = {
    uTime: { value: 0 },
    uMouse: { value: new THREE.Vector2(0, 0) },
    uMouseStrength: { value: 0 },
    uScrollVel: { value: 0 },
    uProgress: { value: 0 },
  };

  // External inputs (set by main.ts each frame)
  private mouseTarget = new THREE.Vector2();
  private mouse = new THREE.Vector2();
  private incomingMouseImpulse = 0;
  private lastPointer = { x: 0, y: 0 };

  constructor() {
    super("hero");
  }

  init(): void {
    const geo = new THREE.PlaneGeometry(8, 5, 96, 96);
    const mat = new THREE.ShaderMaterial({
      vertexShader,
      fragmentShader,
      uniforms: this.uniforms,
    });
    this.mesh = new THREE.Mesh(geo, mat);
    this.mesh.position.set(0, 0, 0);
    this.group.add(this.mesh);

    // Attach pointer listener once; toggle via active flag
    const onMove = (e: PointerEvent) => {
      const x = (e.clientX / window.innerWidth) * 2 - 1;
      const y = (1 - e.clientY / window.innerHeight) * 2 - 1;
      this.mouseTarget.set(x * 3, y * 1.8);

      const dx = e.clientX - this.lastPointer.x;
      const dy = e.clientY - this.lastPointer.y;
      this.lastPointer.x = e.clientX;
      this.lastPointer.y = e.clientY;
      const v = Math.min(Math.hypot(dx, dy) / 20, 1);
      this.incomingMouseImpulse += v * 0.1;
    };
    window.addEventListener("pointermove", onMove, { passive: true });
    this.disposers.push(() => window.removeEventListener("pointermove", onMove));
  }

  update(deltaSeconds: number, _globalProgress: number, localProgress: number | undefined): void {
    this.uniforms.uTime.value += deltaSeconds;

    // Mouse lerp — soft tracking
    this.mouse.lerp(this.mouseTarget, 0.08);
    this.uniforms.uMouse.value.copy(this.mouse);

    // Accumulate + decay mouse strength
    this.uniforms.uMouseStrength.value =
      Math.min(this.uniforms.uMouseStrength.value + this.incomingMouseImpulse, 1) * 0.93;
    this.incomingMouseImpulse = 0;

    if (localProgress !== undefined) {
      this.uniforms.uProgress.value = THREE.MathUtils.lerp(
        this.uniforms.uProgress.value,
        localProgress,
        0.1,
      );
    }
  }

  /** Called by main.ts: feed scroll velocity in [-1, 1] */
  setScrollVelocity(v: number): void {
    this.uniforms.uScrollVel.value = THREE.MathUtils.lerp(
      this.uniforms.uScrollVel.value,
      Math.min(Math.abs(v), 1),
      0.1,
    );
  }
}
